import { useState } from 'react';
import { NJButton, NJHeading } from '@engie-group/fluid-design-system-react';
import { RightSideModal } from './RightSideModalFixed';

interface ChillerCreateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: ChillerFormData) => void;
    plantId: string;
}

interface ChillerFormData {
    name: string;
    type: 'centrifuge' | 'absorption' | 'screw';
    coolingCapacity: number;
    powerConsumption: number;
    cop: number;
    minLoad: number;
    maxLoad: number;
}

const INPUT_STYLE: React.CSSProperties = {
    width: '100%', minHeight: 40, padding: '10px 12px',
    border: '1px solid var(--nj-semantic-color-border-neutral-minimal-default)',
    borderRadius: 4, fontSize: 14, boxSizing: 'border-box',
};

const LABEL_STYLE: React.CSSProperties = {
    display: 'block', fontSize: 14, marginBottom: 6,
};

export function ChillerCreateModal({ isOpen, onClose, onSubmit, plantId: _plantId }: ChillerCreateModalProps) {
    const [formData, setFormData] = useState<ChillerFormData>({
        name: '',
        type: 'centrifuge',
        coolingCapacity: 1000,
        powerConsumption: 500,
        cop: 5.0,
        minLoad: 20,
        maxLoad: 100,
    });

    const handleSubmit = () => {
        if (!formData.name) {
            alert('Veuillez remplir tous les champs obligatoires');
            return;
        }
        onSubmit(formData);
        setFormData({
            name: '',
            type: 'centrifuge',
            coolingCapacity: 1000,
            powerConsumption: 500,
            cop: 5.0,
            minLoad: 20,
            maxLoad: 100,
        });
    };

    return (
        <RightSideModal
            isOpen={isOpen}
            onClose={onClose}
            title="Créer un moyen de production"
            metadata={<>Créé par PROTOTYPER le {new Date().toLocaleDateString('fr-FR')}</>}
            footer={
                <>
                    <NJButton label="Annuler" emphasis="subtle" onClick={onClose} />
                    <NJButton label="Créer" emphasis="bold" onClick={handleSubmit} />
                </>
            }
        >
            {/* Section: Informations générales */}
            <div style={{ marginBottom: 24 }}>
                <NJHeading as="h4" scale="sm" style={{ marginBottom: 16 }}>
                    Informations générales
                </NJHeading>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Nom *</label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Chiller 1"
                        style={INPUT_STYLE}
                    />
                    <span style={{ fontSize: 12, color: 'var(--nj-semantic-color-text-neutral-tertiary-default)', marginTop: 4, display: 'block' }}>
                        {formData.name.length}/64
                    </span>
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Type *</label>
                    <select
                        value={formData.type}
                        onChange={(e) => setFormData({ ...formData, type: e.target.value as ChillerFormData['type'] })}
                        style={INPUT_STYLE}
                    >
                        <option value="centrifuge">Centrifuge</option>
                        <option value="absorption">Absorption</option>
                        <option value="screw">Screw</option>
                    </select>
                </div>
            </div>

            {/* Section: Performance */}
            <div style={{ marginBottom: 24 }}>
                <NJHeading as="h4" scale="sm" style={{ marginBottom: 16 }}>
                    Performance
                </NJHeading>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Puissance frigorifique (kW) *</label>
                    <input
                        type="number"
                        step="1"
                        value={formData.coolingCapacity}
                        onChange={(e) => setFormData({ ...formData, coolingCapacity: Number(e.target.value) })}
                        placeholder="1000"
                        style={INPUT_STYLE}
                    />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>Puissance électrique (kW) *</label>
                    <input
                        type="number"
                        step="1"
                        value={formData.powerConsumption}
                        onChange={(e) => setFormData({ ...formData, powerConsumption: Number(e.target.value) })}
                        placeholder="500"
                        style={INPUT_STYLE}
                    />
                </div>

                <div style={{ marginBottom: 16 }}>
                    <label style={LABEL_STYLE}>COP (Coefficient de Performance) *</label>
                    <input
                        type="number"
                        step="0.1"
                        value={formData.cop}
                        onChange={(e) => setFormData({ ...formData, cop: Number(e.target.value) })}
                        placeholder="5.0"
                        style={INPUT_STYLE}
                    />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                        <label style={LABEL_STYLE}>Charge minimale (%) *</label>
                        <input
                            type="number"
                            step="1"
                            value={formData.minLoad}
                            onChange={(e) => setFormData({ ...formData, minLoad: Number(e.target.value) })}
                            placeholder="20"
                            style={INPUT_STYLE}
                        />
                    </div>
                    <div>
                        <label style={LABEL_STYLE}>Charge maximale (%) *</label>
                        <input
                            type="number"
                            step="1"
                            value={formData.maxLoad}
                            onChange={(e) => setFormData({ ...formData, maxLoad: Number(e.target.value) })}
                            placeholder="100"
                            style={INPUT_STYLE}
                        />
                    </div>
                </div>
            </div>
        </RightSideModal>
    );
}
